import _ from 'lodash';
import dayjs from 'dayjs';
import PlanChangeModal from '~/components/members-area/PlanChangeModal.vue';
import SlideToggle from '~/components/common/SlideToggle.vue';
import { getPlanByName } from '~/utils/plans';
export default {
  name: 'ConfirmationScreen',
  components: {
    PlanChangeModal,
    SlideToggle
  },
  props: {
    title: String,
    policies: Object,
    plans: {
      type: Array,
      required: true
    },
    plan: Object,
    planHeaderText: String,
    allowPlanChange: Boolean,
    allowAutopayChange: Boolean,
    planAutopay: Boolean,
    showChaPolicy: {
      type: Boolean,
      default: true
    },
    showAchPolicy: {
      type: Boolean,
      default: true
    },
    continueButtonAction: Function,
    continueButtonText: String,
    scheduledPayment: {
      type: Boolean,
      default: false
    },
    isCau: {
      type: Boolean,
      default: false
    },
    currentPlanName: {
      // for CAU users, to display their actual enrolled plan instead of the current available plans
      type: String,
      required: false
    },
    metadata: Object,
    memberProfile: {
      // AnalyticsHub events will not be tracked if this is not provided
      type: Object,
      required: false
    },
    planType: {
      type: String,
      required: false
    },
    usePatriot: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showModal: false,
      submitting: false,
      balanceCheck: {},
      selectedAutopay: this.planAutopay,
      showDisclosure: false,
      disclosureFile: '',
      trackedStartedEvent: false,
      checkingInsufficientFunds: false
    };
  },
  computed: {
    modalPlan: {
      get() {
        return this.plan;
      },
      set(newVal) {
        this.$emit('update:plan', newVal);
      }
    },
    newCardholder() {
      return !this.isCau && !this.scheduledPayment;
    },
    selectedPlan() {
      if (this.currentPlanName && !this.allowPlanChange) {
        return getPlanByName(this.currentPlanName);
      }
      return this.plans[this.plan.selectedPlan];
    },
    formattedProcessDate() {
      return this.metadata && this.metadata.raw_process_date.format('MMMM Do, YYYY');
    },
    formattedNextPaymentDate() {
      if (!this.metadata) return '';
      const date = dayjs(this.metadata.raw_process_date).add(1, this.plan.billingCycle === 1 ? 'years' : 'months');
      return date.format('MMMM Do, YYYY');
    },
    policyCheckboxes: {
      get() {
        return Object.keys(this.policies);
      },
      set(newValue) {
        // remove unchecked policies
        const newPolicies = _.pick(this.policies, newValue);

        // add newly checked policies
        newValue.forEach(key => {
          if (!newPolicies[key]) newPolicies[key] = new Date();
        });

        // these ride or die together
        if (newPolicies.patriot_cardholder_agreement) {
          // patriot
          newPolicies.standing_ach_authorization = new Date(newPolicies.patriot_cardholder_agreement);
        } else if (newPolicies.cardholder_agreement) {
          // evolve
          newPolicies.standing_ach_authorization = new Date(newPolicies.cardholder_agreement);
        } else {
          delete newPolicies.standing_ach_authorization;
        }
        this.$emit('update:policies', newPolicies);
        if (this.memberProfile && !this.trackedStartedEvent) {
          this.$analyticsHub.track('Membership Confirmation Started', {
            member_id: this.memberProfile.id,
            user_id: this.memberProfile.extra_user_id,
            plan_type: this.planType,
            plan_price: this._props.metadata.formatted_plan_cost,
            plan_frequency: this._props.metadata.billing_cycle
          });
          this.trackedStartedEvent = true;
        }
      }
    },
    readyToSubmit() {
      return !this.submitting && (!this.showChaPolicy || (this.policies.patriot_cardholder_agreement || this.policies.cardholder_agreement) && this.policies.standing_ach_authorization) && (!this.showAchPolicy || (this.selectedAutopay ? this.policies.recurring_ach_authorization : this.policies.one_time_ach_authorization));
    },
    continueButtonClasses() {
      const light = document.documentElement.classList.contains('theme-light');
      const marginTop = this.newCardholder ? 'mt-8' : 'mt-16';
      const commonClasses = [(this.showChaPolicy || this.showAchPolicy) && marginTop];
      const notReadyClasses = [light && 'opacity-10'];
      return commonClasses.concat(!this.readyToSubmit ? notReadyClasses : []);
    }
  },
  watch: {
    selectedAutopay() {
      // clear policy checkboxes (even the ones that stay the same)
      this.policyCheckboxes = [];
      this.$emit('update:planAutopay', this.selectedAutopay);
    }
  },
  mounted() {
    if (this.memberProfile) {
      this.$analyticsHub.track('Membership Confirmation Viewed', {
        member_id: this.memberProfile.id,
        user_id: this.memberProfile.extra_user_id,
        plan_type: this.planType,
        plan_price: this._props.metadata.formatted_plan_cost,
        plan_frequency: this._props.metadata.billing_cycle
      });
    }
  },
  methods: {
    async checkInsufficientFunds(upfrontPlanPrice) {
      if (this.checkingInsufficientFunds) {
        console.log('[CheckInsufficientFunds][Debounce] already checking...');
        return;
      }
      this.checkingInsufficientFunds = true;
      try {
        if (this.isCau || this.scheduledPayment) {
          // skip balance check for members that are already enrolled
          this.balanceCheck = {
            success: true,
            insufficient: false
          };
          return;
        }
        try {
          this.balanceCheck = await this.$axios.$post('/api/members/plaid/balance-check', {
            upfrontPlanPrice,
            fetchLatest: true
          });
        } catch (err) {
          console.error(`checkInsufficientFunds error for MID ${this.memberProfile.id}`, err);
          this.showModal = false;
          if (err.response.status === 403) {
            await this.$nextTick();
            this.$emit('relinkPlaid', err);
          } else {
            throw err;
          }
        }
      } finally {
        this.checkingInsufficientFunds = false;
      }
    },
    async trySubmit() {
      this.submitting = true;
      await this.checkInsufficientFunds(this.selectedPlan.price[this.plan.billingCycle]);
      if (this.balanceCheck.success) {
        if (this.balanceCheck.insufficient) {
          if (this.balanceCheck.bypassPlaidBalanceCheck) {
            // send the balance check object to the card page so it can be used for event tracking
            this.$emit('setBalanceCheck', this.balanceCheck);
            this.continueButtonAction();
          } else {
            this.showModal = true;
          }
        } else {
          this.continueButtonAction();
        }
      }
      this.submitting = false;
    }
  }
};